/**
 * Handle VGS payment context.
 */
import { h } from 'preact';
import {
	useContext, useReducer, createContext, useEffect, useState, useMemo,
} from 'preact/compat';
import { Theme } from '../lib/render';

const vgsInitValues = {
	Account: {
		error: false,
		touched: false,
	},
	ExpirationDate: {
		error: false,
		touched: false,
	},
	SecurityCode: {
		error: false,
		touched: false,
	},
};

const vgsInitCagingValues = {
	Account: {
		error: false,
		touched: false,
	},
	ExpirationDate: {
		error: false,
		touched: false,
	},
};

interface VGSError {
	error: string | boolean;
	touched: boolean;
}

export enum VGSTypes {
	Account = 'Account',
	ExpirationDate = 'ExpirationDate',
	SecurityCode = 'SecurityCode'
}

export type VGSErrors = {
	Account: VGSError;
	ExpirationDate: VGSError;
	SecurityCode?: VGSError;
}

type VgsActionPayload = {
	type: 'ERROR';
	payload: {
		name: string;
		error: string | boolean;
	}
}|{
	type: 'TOUCH';
	payload: {
		name: string;
		touched: boolean;
	}
}|{
	type: 'TOUCH_ALL';
}|{
	type: 'RESET';
}|{
	type: 'RESET_CAGING';
};

const vgsReducer = (state: VGSErrors, action: VgsActionPayload) => {
	const newState = { ...state };
	switch (action.type) {
	case 'ERROR':
		return {
			...state,
			[action.payload.name]: {
				...state[action.payload.name as VGSTypes],
				error: action.payload.error,
			},
		};
	case 'TOUCH':
		return {
			...state,
			[action.payload.name]: {
				...state[action.payload.name as VGSTypes],
				touched: action.payload.touched,
			},
		};
	case 'TOUCH_ALL':
		Object.keys(state).forEach((key: VGSTypes) => {
			newState[key] = {
				...state[key],
				touched: true,
			};
		});
		return newState;
	/**
	 * Generic reset.
	 */
	case 'RESET':
		return vgsInitValues;
	/**
	 * Caging reset.
	 * Caging forms do not require a security code.
	 */
	case 'RESET_CAGING':
		return vgsInitCagingValues;
	default:
		return state;
	}
};

export const VGS = createContext<{
	vgsErrors: VGSErrors;
	dispatchVgs:(payload: VgsActionPayload) => void;
	vgsValid: boolean;
	vgsInit: any;
		} | undefined>(undefined);

/**
 * VGS Provider.
 */
const VGSProvider = ({ children }: { children: h.JSX.Element }) => {
	const theme = useContext(Theme);

	/**
	 * Determine initial values.
	 * Caging forms do not require a security code.
	 * See payment.tsx for more info.
	 */
	const [vgsErrors, dispatchVgs] = useReducer(vgsReducer, theme.donate_type === 'caging' ? vgsInitCagingValues : vgsInitValues);
	const [vgsValid, setVgsValid] = useState(false);

	/**
	 * vgsInit renders an iframe, to ensure value persistence,
	 * we need to prevent unmounting/rerendering of the iframe.
	 */
	const vgsInit = useMemo(() => window.submitjs.createForm(), [window.submitjs]);

	/**
	 * Validate vgs status.
	 */
	useEffect(() => {
		if (Object.values(vgsErrors).every((v: VGSError) => v.error === false && v.touched === true)) {
			setVgsValid(true);
		} else {
			setVgsValid(false);
		}
	}, [vgsErrors]);

	const vgsContext = useMemo(() => ({
		vgsErrors,
		dispatchVgs,
		vgsValid,
		vgsInit,
	}), [vgsErrors, vgsValid, vgsInit]);

	return (
		<VGS.Provider value={vgsContext}>
			{children}
		</VGS.Provider>
	);
};

const useVGS = () => {
	const context = useContext(VGS);
	if (context === undefined) {
		throw new Error('useVGS must be used within a VGSProvider');
	}
	return context;
};

export { VGSProvider, useVGS };
